import React from 'react';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';

const MINIMAP_WIDTH = 150;
const MINIMAP_HEIGHT = 100;

interface MinimapProps {
  canvasWidth: number;
  canvasHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  visible?: boolean;
}

const Minimap: React.FC<MinimapProps> = ({
  canvasWidth,
  canvasHeight,
  viewportWidth,
  viewportHeight,
  visible = true,
}) => {
  const { layout } = useStageStore();
  const { zoom, panOffset, setPanOffset } = useEditorStore();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Calculate scale for minimap
  const scaleX = MINIMAP_WIDTH / canvasWidth;
  const scaleY = MINIMAP_HEIGHT / canvasHeight;

  // Draw minimap
  React.useEffect(() => {
    if (!canvasRef.current || !layout) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Draw border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Draw grid
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 0.5;
    const gridSize = 1; // 1 meter grid
    const scaledGridSize = gridSize * scaleX;

    for (let x = 0; x < MINIMAP_WIDTH; x += scaledGridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, MINIMAP_HEIGHT);
      ctx.stroke();
    }

    for (let y = 0; y < MINIMAP_HEIGHT; y += scaledGridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(MINIMAP_WIDTH, y);
      ctx.stroke();
    }

    // Draw objects
    layout.objects.forEach((obj) => {
      const x = obj.position.x * scaleX;
      const y = obj.position.y * scaleY;

      // Choose color based on type
      let color = '#3b82f6'; // blue for paper
      if (obj.type === 'steel_target' || obj.type === 'plate') color = '#6b7280'; // gray
      if (obj.type === 'popper') color = '#fbbf24'; // yellow
      if (obj.type === 'no_shoot') color = '#ef4444'; // red
      if (obj.type.includes('position')) color = '#10b981'; // green
      if (obj.type.includes('cover') || obj.type === 'barrel' || obj.type === 'table')
        color = '#8b5cf6'; // purple

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw walls
    (layout.walls || []).forEach((wall) => {
      if (!wall.points || wall.points.length < 2) return;

      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 1.5;
      ctx.beginPath();

      wall.points.forEach((point, index) => {
        const x = point.x * scaleX;
        const y = point.y * scaleY;
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();
    });

    // Draw viewport rectangle
    const viewportScaleX = (viewportWidth / canvasWidth) * zoom;
    const viewportScaleY = (viewportHeight / canvasHeight) * zoom;
    const viewportX = (-panOffset.x / canvasWidth) * MINIMAP_WIDTH;
    const viewportY = (-panOffset.y / canvasHeight) * MINIMAP_HEIGHT;
    const viewportW = MINIMAP_WIDTH * viewportScaleX;
    const viewportH = MINIMAP_HEIGHT * viewportScaleY;

    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 2;
    ctx.strokeRect(viewportX, viewportY, viewportW, viewportH);
  }, [layout, zoom, panOffset, canvasWidth, canvasHeight, viewportWidth, viewportHeight, scaleX, scaleY]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;

    // Center the view on the clicked point
    const newPanX = x - viewportWidth / 2;
    const newPanY = y - viewportHeight / 2;

    setPanOffset({ x: newPanX, y: newPanY });
  };

  if (!visible) return null;

  return (
    <div className="absolute bottom-3 right-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg p-1">
      <canvas
        ref={canvasRef}
        width={MINIMAP_WIDTH}
        height={MINIMAP_HEIGHT}
        onClick={handleClick}
        className="block bg-gray-50 dark:bg-gray-900 cursor-pointer border border-gray-200 dark:border-gray-700"
      />
      <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1 px-1">
        Click to pan
      </div>
    </div>
  );
};

export default Minimap;
